import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	makeStyles,
} from "@material-ui/core";
import { CssRowFieldCellWrapper } from "components/fields/CssFieldCellWrapper";
import { OrderReferenceField } from "components/fields/OrderReferenceField";
import { SsccFieldWithBarcode } from "components/fields/SsccFieldWithBarcode";
import React from "react";
import { ArrayField, Datagrid, FunctionField, TextField, useTranslate } from "react-admin";
import { formatWeight } from "utils/weight";

const useRowStyles = makeStyles({
	movedRow: { opacity: 0.5, pointerEvents: "none" },
});

export const BoxesDialog = ({
	open,
	onClose,
	record,
	isPickMode = false,
	isManualOrCombinedMode = true,
	pickedBoxIds = [],
	onPickBox,
	onUnpickBox,
	setBarcodeValue,
	setBarcodeTitle,
	setBarcodeDialogOpen,
}) => {
	const translate = useTranslate();
	const classes = useRowStyles();

	if (!record) return null;

	const pickedSet = new Set(pickedBoxIds);

	const toggleBox = (box) => {
		if (pickedSet.has(box.id)) {
			onUnpickBox(record.id, box.id);
		} else {
			onPickBox(record, box, false);
		}
	};

	const selectableBoxes = (record.boxes || []).filter((box) => !box.is_moved);
	const allSelected =
		selectableBoxes.length > 0 && selectableBoxes.every((box) => pickedSet.has(box.id));
	const someSelected = selectableBoxes.some((box) => pickedSet.has(box.id));

	const toggleSelectAll = () => {
		if (allSelected) {
			selectableBoxes.forEach((box) => onUnpickBox(record.id, box.id));
		} else {
			selectableBoxes.forEach((box) => onPickBox(record, box, false));
		}
	};

	const boxRowClass = (record) => (record?.is_moved ? [classes.movedRow] : []);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
			<DialogTitle>
				{translate("tab.pallets.related_boxes_title", { id: record.id })}
				{isPickMode && (
					<FormControlLabel
						style={{ marginLeft: 16 }}
						control={
							<Checkbox
								checked={allSelected}
								indeterminate={!allSelected && someSelected}
								disabled={selectableBoxes.length === 0}
								onChange={toggleSelectAll}
							/>
						}
						label={translate("osfc.buttons.select_all")}
					/>
				)}
			</DialogTitle>
			<DialogContent>
				<ArrayField source="boxes" record={record}>
					<Datagrid>
						<CssRowFieldCellWrapper cssFn={boxRowClass} />
						{isPickMode && (
							<FunctionField
								label=""
								render={(record) => (
									<Checkbox
										checked={pickedSet.has(record.id)}
										disabled={record?.is_moved || !isManualOrCombinedMode}
										onClick={(e) => e.stopPropagation()}
										onChange={() => toggleBox(record)}
									/>
								)}
							/>
						)}
						<TextField source="id" label="tab.picking_list.box_id" />
						<SsccFieldWithBarcode
							source="sscc"
							label="tab.pallets.sscc"
							titleKey="tab.picking_list.box_id"
							setBarcodeValue={setBarcodeValue}
							setBarcodeTitle={setBarcodeTitle}
							setBarcodeDialogOpen={setBarcodeDialogOpen}
						/>
						<FunctionField
							source="net_weight"
							label="tab.pallets.net_weight"
							render={(record) => formatWeight(record?.net_weight, record?.weight_unit_code)}
						/>
						<TextField source="ext_box_id" label="tab.pallets.external_id" />
						<OrderReferenceField source="incoming_order_id" label="tab.pallets.incoming_order" />
						<TextField source="source_pallet_sscc" label="tab.picking_list.source_sscc" />
						<TextField source="product_number" label="tab.pallets.product_number" />
						<TextField source="product_name" label="tab.pallets.product_name" />
					</Datagrid>
				</ArrayField>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="primary">
					{translate("ra.action.close")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
